import { useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { Form, FormActions, FormInput } from "../Controls/Form";
import CustomButton from "../Controls/CustomButton";
import ListIconButton from "../Controls/ListIconButton";
import EducationClassEditModal from "../Modals/EducationClassEditModal";
import UserData from "../Controls/UserData";
import { toast } from "react-toastify";

function emptyEducation() {
  return {
    ID: 0,
    InstitutionName: "",
    Degree: "",
    YearStart: null,
    YearEnd: null,
    Honors: "",
    Classes: [],
  };
}

function normalizeClass(c, userData) {
  const skillIds = (c.SkillIDs || c.skillIDs || []).map((id) => Number(id)).filter((id) => id > 0);
  const personSkills = userData?.personSkills || userData?.PersonSkills || [];
  const catalog = userData?.skills || [];
  const skills = skillIds.map((id) => {
    const fromPerson = personSkills.find(
      (ps) => Number(ps.SkillID ?? ps.skillID) === id
    );
    const fromCatalog = catalog.find((s) => Number(s.ID ?? s.id) === id);
    return {
      SkillID: id,
      SkillName:
        fromPerson?.SkillName ||
        fromPerson?.skillName ||
        fromPerson?.Name ||
        fromCatalog?.Name ||
        fromCatalog?.name ||
        `Skill #${id}`,
    };
  });
  return {
    ID: c.ID || c.id || 0,
    Name: c.Name || c.name || "",
    Grade: c.Grade || c.grade || "",
    Number: c.Number || c.number || "",
    TaughtBy: c.TaughtBy || c.taughtBy || "",
    StartDate: c.StartDate || c.startDate || null,
    EndDate: c.EndDate || c.endDate || null,
    SkillIDs: skillIds,
    Skills: c.Skills || c.skills || skills,
  };
}

/**
 * Education form body. Prefer EducationEditModal for UI entry points.
 */
export default function EducationEdit({ educationId = 0, onClose }) {
  const id = Number.parseInt(educationId, 10) || 0;
  const [userData] = useState(UserData.UserData() || {});
  const [education, updateEducation] = useState(() => {
    if (!id) return emptyEducation();
    const list = userData.education || [];
    const found = list.find((e) => Number(e.ID || e.id) === id);
    if (!found) return emptyEducation();
    return {
      ...found,
      Classes: (found.Classes || found.classes || []).map((c) => normalizeClass(c, userData)),
    };
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("institution");
  /** null = closed; -1 = add; >=0 = edit index */
  const [classModalIndex, setClassModalIndex] = useState(null);

  function close() {
    onClose?.();
  }

  function removeClass(index) {
    const next = [...(education.Classes || [])];
    next.splice(index, 1);
    updateEducation({ ...education, Classes: next });
  }

  function onClassSave(cls) {
    const next = [...(education.Classes || [])];
    if (classModalIndex != null && classModalIndex >= 0) {
      next[classModalIndex] = { ...next[classModalIndex], ...cls };
    } else {
      next.push(cls);
    }
    updateEducation({ ...education, Classes: next });
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...education,
        ID: education.ID || 0,
        YearStart:
          education.YearStart === "" || education.YearStart == null
            ? null
            : Number(education.YearStart),
        YearEnd:
          education.YearEnd === "" || education.YearEnd == null
            ? null
            : Number(education.YearEnd),
        Classes: (education.Classes || [])
          .filter((c) => (c.Name || c.name || "").trim())
          .map((c) => ({
            ID: c.ID || c.id || 0,
            Name: (c.Name || c.name || "").trim(),
            Grade: c.Grade || c.grade || null,
            Number: c.Number || c.number || "",
            TaughtBy: c.TaughtBy || c.taughtBy || null,
            StartDate: c.StartDate || c.startDate || null,
            EndDate: c.EndDate || c.endDate || null,
            SkillIDs: (c.SkillIDs || c.skillIDs || (c.Skills || []).map((s) => s.SkillID || s.skillID))
              .map((sid) => Number(sid))
              .filter((sid) => sid > 0),
          })),
      };
      const ret = await UserData.UpdateAsync("education", payload, true);
      if (ret?.ok || ret?.result === "Success") {
        toast.success("Education saved");
        await UserData.GetUserDataAsync();
        close();
      } else {
        toast.error(ret?.message || "Could not save education");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const eduId = education.ID || education.id;
    if (!eduId) {
      close();
      return;
    }
    const resp = await UserData.DeleteAsync("education", eduId);
    if (resp?.result) {
      toast.success("Education deleted");
      await UserData.GetUserDataAsync();
      close();
    } else {
      toast.error(resp?.error || "Delete failed");
    }
  }

  const classes = education.Classes || [];
  const classCount = classes.filter((c) => (c.Name || c.name || "").trim()).length;
  const classModalOpen = classModalIndex !== null;
  const editingClass =
    classModalIndex != null && classModalIndex >= 0 ? classes[classModalIndex] : null;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={education} onUpdate={updateEducation}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Education sections">
          <button
            type="button"
            role="tab"
            id="education-tab-institution"
            aria-selected={tab === "institution"}
            aria-controls="education-panel-institution"
            className={tab === "institution" ? "is-active" : undefined}
            data-testid="education-tab-institution"
            onClick={() => setTab("institution")}
          >
            Institution
          </button>
          <button
            type="button"
            role="tab"
            id="education-tab-classes"
            aria-selected={tab === "classes"}
            aria-controls="education-panel-classes"
            className={tab === "classes" ? "is-active" : undefined}
            data-testid="education-tab-classes"
            onClick={() => setTab("classes")}
          >
            Classes{classCount > 0 ? ` (${classCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="education-panel-institution"
            role="tabpanel"
            aria-labelledby="education-tab-institution"
            aria-hidden={tab !== "institution"}
            className={`cm-modal-tab-panel${tab === "institution" ? " is-active" : ""}`}
            {...(tab !== "institution" ? { inert: "" } : {})}
          >
            <FormInput label="Institution Name" field="InstitutionName" />
            <FormInput label="Degree" field="Degree" />
            <FormInput label="Year Start" field="YearStart" type="number" />
            <FormInput label="Year End" field="YearEnd" type="number" />
            <FormInput label="Honors" field="Honors" />
          </div>

          <div
            id="education-panel-classes"
            role="tabpanel"
            aria-labelledby="education-tab-classes"
            aria-hidden={tab !== "classes"}
            className={`cm-modal-tab-panel education-classes${tab === "classes" ? " is-active" : ""}`}
            {...(tab !== "classes" ? { inert: "" } : {})}
          >
            <p className="fieldHint">Optional course details for early-career profiles.</p>
            {classes.length === 0 ? (
              <p className="fieldHint">No classes added yet.</p>
            ) : (
              <ul className="education-class-list">
                {classes.map((c, idx) => {
                  const name = (c.Name || c.name || "").trim() || "Untitled class";
                  return (
                    <li key={c.ID || c.id || `new-${idx}`} className="education-class-row">
                      <button
                        type="button"
                        className="education-class-name"
                        data-testid={`education-class-edit-${idx}`}
                        title={`Edit ${name}`}
                        onClick={() => setClassModalIndex(idx)}
                      >
                        {name}
                      </button>
                      <ListIconButton
                        aria-label={`Remove ${name}`}
                        title="Remove class"
                        data-testid={`education-class-remove-${idx}`}
                        onClick={() => removeClass(idx)}
                      >
                        <FaTrashAlt aria-hidden />
                      </ListIconButton>
                    </li>
                  );
                })}
              </ul>
            )}
            <CustomButton
              type="button"
              variant="contained"
              color="primary"
              data-testid="education-class-add"
              onClick={() => setClassModalIndex(-1)}
            >
              <MdAdd aria-hidden /> Add Class
            </CustomButton>
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            (education.ID || education.id) > 0
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
        />
      </Form>

      {classModalOpen ? (
        <EducationClassEditModal
          isNew={classModalIndex < 0}
          initialClass={editingClass}
          onSave={onClassSave}
          onClose={() => setClassModalIndex(null)}
        />
      ) : null}
    </div>
  );
}
